import { createDelta } from '../../../../domain/value-object/ItemDeltaInputVO'
import {
  CreateTicketInput,
  UpdateTicketDeltaInput,
  TicketDetail,
} from '../../../../lib/functions/ticket'
import { isTagColumnEdited } from '../../../../lib/functions/tag'
import {
  isWatcherEdited,
  WbsItemBasic,
  WbsItemDeltaInput,
  WbsItemDetail,
} from '../../../../lib/functions/wbsItem'
import { hasDiffDateTerm } from '../../../../utils/date'
import { generateUuid } from '../../../../utils/uuids'
import { NewWbsItemRow } from '../../ProjectPlanNew/projectPlanNew'
import { TicketRow, TicketWbsItemRow } from '../tickets'
import { TicketListBasic } from '../../../../lib/functions/ticketList'
import { wbsItemAdditionalPropertyValuesVoService } from '../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { WbsItemCodeGenerator } from '../../../../applications/ports/wbsItemCodeService'
import store from '../../../../store'
import {
  WbsItemStatus,
  WbsItemType,
} from '../../../../domain/entity/WbsItemEntity'
import { ProjectPlanCumulation } from '../../../../lib/functions/projectPlan'
import { CUSTOM_ENUM_NONE } from '../../../../lib/commons/customEnum'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import { CommentSummary } from '../../../../store/comments'
import { AttachmentSummary } from '../../../../utils/attachment'
import { UserBasic } from '../../../../domain/value-object/UserBasicVO'
import Workload from '../../../../lib/functions/workload'

export const generateCreateTicketInput = ({
  projectUuid,
  row,
  prevSiblingUuid,
}: {
  projectUuid: string
  row: TicketRow
  prevSiblingUuid: string | undefined
}): CreateTicketInput => {
  const wbsItem: TicketWbsItemRow | undefined = row.wbsItem
  if (!wbsItem) return {} as CreateTicketInput

  return {
    uuid: row.uuid,
    ticketType: wbsItem.ticketType!,
    ticketListUuid: row.ticketList?.uuid!,
    prevSiblingUuid,
    parentWbsItemUuid: row.parentWbsItem?.uuid!,
    projectPlan: {
      projectUuid,
      uuid: generateUuid(),
      type: wbsItem.type,
      ticketType: wbsItem.ticketType,
      wbsItem: {
        ...wbsItem,
        typeUuid: wbsItem.wbsItemType.uuid,
        displayName: wbsItem.displayName ?? '',
        description: wbsItem.description ?? '',
        teamUuid: wbsItem.team?.uuid,
        accountableUuid: wbsItem.accountable?.uuid,
        responsibleUuid: wbsItem.responsible?.uuid,
        assigneeUuid: wbsItem.assignee?.uuid,
        watchers: wbsItem.watchers?.map(v => v.uuid),
        scheduledDate: {
          startDate: wbsItem.scheduledDate?.startDate,
          endDate: wbsItem.scheduledDate?.endDate,
        },
        actualDate: {
          startDate: wbsItem.actualDate?.startDate,
          endDate: wbsItem.actualDate?.endDate,
        },
        sprintUuid: wbsItem.sprint?.uuid,
        additionalPropertyValues: wbsItem.additionalPropertyValues
          ? wbsItemAdditionalPropertyValuesVoService.serialize(
              wbsItem.additionalPropertyValues
            )
          : undefined,
      },
      productBacklogItem: false,
    },
  }
}

export const generateUpdateWbsItemDeltaInput = (
  original: NewWbsItemRow,
  wbsItem: NewWbsItemRow
): WbsItemDeltaInput => {
  return {
    uuid: wbsItem.uuid,
    type: wbsItem.type,
    status: createDelta(original.status, wbsItem.status),
    substatus: createDelta(original.substatus, wbsItem.substatus),
    displayName: createDelta(original.displayName, wbsItem.displayName),
    description: createDelta(original.description, wbsItem.description),
    teamUuid: createDelta(original.team?.uuid, wbsItem.team?.uuid),
    accountableUuid: createDelta(
      original.accountable?.uuid,
      wbsItem.accountable?.uuid
    ),
    responsibleUuid: createDelta(
      original.responsible?.uuid,
      wbsItem.responsible?.uuid
    ),
    assigneeUuid: createDelta(original.assignee?.uuid, wbsItem.assignee?.uuid),
    estimatedStoryPoint: createDelta(
      original.estimatedStoryPoint,
      wbsItem.estimatedStoryPoint
    ),
    estimatedHour: createDelta(original.estimatedHour, wbsItem.estimatedHour),
    priority: createDelta(original.priority, wbsItem.priority),
    difficulty: createDelta(original.difficulty, wbsItem.difficulty),
    scheduledDate: hasDiffDateTerm(
      original.scheduledDate,
      wbsItem.scheduledDate
    )
      ? {
          oldValue: original.scheduledDate,
          newValue: wbsItem.scheduledDate,
        }
      : undefined,
    actualDate: hasDiffDateTerm(original.actualDate, wbsItem.actualDate)
      ? {
          oldValue: original.actualDate,
          newValue: wbsItem.actualDate,
        }
      : undefined,
    estimatedAmount: createDelta(
      original.estimatedAmount,
      wbsItem.estimatedAmount
    ),
    actualAmount: createDelta(original.actualAmount, wbsItem.actualAmount),
    sprintUuid: createDelta(original.sprint?.uuid, wbsItem.sprint?.uuid),
    additionalPropertyValues: wbsItem.additionalPropertyValues
      ? wbsItemAdditionalPropertyValuesVoService.serializeDelta(
          original.additionalPropertyValues,
          wbsItem.additionalPropertyValues
        )
      : undefined,
  } as WbsItemDeltaInput
}

export const generateUpdateTicketDeltaInput = ({
  originalRow,
  currentRow,
  originalPrevSiblingUuid,
  prevSiblingUuid,
}: {
  originalRow: TicketRow
  currentRow: TicketRow
  originalPrevSiblingUuid: string | undefined
  prevSiblingUuid: string | undefined
}): UpdateTicketDeltaInput => {
  return {
    uuid: currentRow.uuid,
    ticketListUuid: createDelta(
      originalRow.ticketList?.uuid,
      currentRow.ticketList?.uuid
    ),
    prevSiblingUuid: createDelta(originalPrevSiblingUuid, prevSiblingUuid),
    wbsItem: generateUpdateWbsItemDeltaInput(
      originalRow.wbsItem!,
      currentRow.wbsItem!
    ),
  }
}

export const gernerateTicketDeleteRequest = (
  deletedRows: TicketRow[],
  getUuidAndLockVersion: (row: TicketRow) => {
    uuid: string | undefined
  }
) => {
  return deletedRows
    .filter((row: TicketRow) => row.wbsItem?.uuid && row.wbsItem?.lockVersion)
    .map((row: TicketRow) => {
      const { uuid } = getUuidAndLockVersion(row)
      return {
        uuid,
        wbsItemUuid: row.wbsItem!.uuid!,
      }
    })
}

export const generateUpdateWathersRequest = (
  originalRows: TicketRow[],
  changedTicketRows: TicketRow[]
) => {
  return changedTicketRows
    .filter((row: TicketRow) => {
      const original =
        originalRows.find(d => d.uuid === row.uuid)?.wbsItem?.watchers ?? []
      const newData = row.wbsItem?.watchers ?? []
      return (
        (row.added || row.edited) &&
        row.wbsItem &&
        isWatcherEdited(original, newData)
      )
    })
    .map((row: TicketRow) => {
      const wbsItem: TicketWbsItemRow = row.wbsItem!
      return {
        uuid: row.uuid,
        wbsItemUuid: wbsItem.uuid,
        userUuids:
          wbsItem.watchers && Array.isArray(wbsItem.watchers)
            ? wbsItem.watchers.map(v => v.uuid)
            : [],
      }
    })
}

export const generateUpdateTagsRequest = (
  originalRows: TicketRow[],
  changedTicketRows: TicketRow[]
) => {
  return changedTicketRows
    .filter((row: TicketRow) => {
      const original =
        originalRows.find(v => v.uuid === row.uuid)?.wbsItem?.tags ?? []
      const newData = row.wbsItem?.tags ?? []
      return (
        (row.added || row.edited) &&
        row.wbsItem &&
        isTagColumnEdited(original, newData)
      )
    })
    .map((row: TicketRow) => {
      const wbsItem: TicketWbsItemRow = row.wbsItem!
      return {
        uuid: row.uuid,
        wbsItemUuid: wbsItem.uuid,
        tagUuids:
          wbsItem.tags && Array.isArray(wbsItem.tags)
            ? wbsItem.tags.map(v => v.uuid)
            : [],
      }
    })
}

interface GenerateRequestParams<TTicketRequest> {
  row?: TicketRow
  ticketRequest?: TTicketRequest
}

export interface GenerateCreateRequestParams
  extends GenerateRequestParams<CreateTicketInput> {}

export const generateCreateRequest = (
  projectUuid: string,
  allData: TicketRow[],
  changed: TicketRow[],
  generateCreateInput: (
    params: GenerateCreateRequestParams
  ) => CreateTicketInput | undefined
): CreateTicketInput[] => {
  return changed
    .filter((row: TicketRow) => row.added && row.wbsItem?.displayName)
    .map((row: TicketRow): CreateTicketInput | undefined => {
      const index = allData.findIndex(d => d.uuid === row.uuid)
      const prevSiblingUuid = index <= 0 ? undefined : allData[index - 1]?.uuid
      const createTicketRequest = generateCreateTicketInput({
        projectUuid,
        row,
        prevSiblingUuid,
      })
      return generateCreateInput({
        row: row,
        ticketRequest: createTicketRequest,
      })
    })
    .filter((d): d is CreateTicketInput => !!d)
}

export interface GenerateUpdateRequestParams
  extends GenerateRequestParams<UpdateTicketDeltaInput> {
  originalRow: TicketRow
}

export const generateUpdateDeltaBatchRequest = (
  originalData: TicketRow[],
  currentData: TicketRow[],
  changed: TicketRow[],
  generateUpdateDeltaInput: (
    params: GenerateUpdateRequestParams
  ) => UpdateTicketDeltaInput | undefined
) => {
  return changed
    .filter((row: TicketRow) => row.edited && !row.added && row.wbsItem)
    .map((row: TicketRow): UpdateTicketDeltaInput | undefined => {
      const originalIndex = originalData.findIndex(d => d.uuid === row.uuid)
      if (originalIndex < 0) return undefined

      const original = originalData[originalIndex]
      const originalPrevSiblingUuid =
        originalIndex <= 0 ? undefined : originalData[originalIndex - 1]?.uuid

      const index = currentData.findIndex(d => d.uuid === row.uuid)
      const prevSiblingUuid =
        index <= 0 ? undefined : currentData[index - 1]?.uuid

      const updateTicketDeltaInput = generateUpdateTicketDeltaInput({
        originalRow: original,
        currentRow: row,
        originalPrevSiblingUuid,
        prevSiblingUuid,
      })
      return generateUpdateDeltaInput({
        originalRow: original,
        row: row,
        ticketRequest: updateTicketDeltaInput,
      })
    })
    .filter(d => !!d)
}

export const createNewTicketRow = (
  ticketList: TicketListBasic,
  generateCode: WbsItemCodeGenerator
): TicketRow => {
  const uuid = generateUuid()
  const project = store.getState().project
  const wbsItemType = project.ticketTypes.find(
    v => v.code === ticketList.ticketType
  )!
  return {
    treeValue: [uuid],
    uuid,
    lockVersion: 0,
    ticketList: {
      uuid: ticketList.uuid,
      code: ticketList.code,
      wbsItemUuid: ticketList.wbsItemUuid,
      ticketType: ticketList.ticketType,
      projectUuid: ticketList.projectUuid,
      displayName: ticketList.displayName,
    },
    wbsItem: {
      projectUuid: project.selected,
      uuid: generateUuid(),
      code: generateCode(),
      type: WbsItemType.TASK,
      wbsItemType: wbsItemType,
      baseWbsItemType: wbsItemType,
      ticketType: ticketList.ticketType,
      ticketListUuid: ticketList.uuid,
      status: WbsItemStatus.TODO,
      scheduledDate: { startDate: undefined, endDate: undefined },
      actualDate: { startDate: undefined, endDate: undefined },
    },
    cumulation: new ProjectPlanCumulation(),
    added: true,
  }
}

// For types with specific parameters
export interface FetchResponseByTypeBase {
  uuid: string
  lockVersion: number
  ticket: TicketDetail
}

export interface TicketDataManager {
  // Fetch -----
  createRowByResponse: (
    response: any,
    dailyWorkHours: number,
    monthlyWorkDays: number
  ) => TicketRow
  // Save -----
  createUpdateDeltaBatchRequest: (
    projectUuid: string,
    originalData: TicketRow[],
    currentData: TicketRow[],
    deletedRows: TicketRow[]
  ) => any
}

export const createTicketRowByResponse = (
  response: any,
  dailyWorkHours: number,
  monthlyWorkDays: number
): TicketRow => {
  const ticketResponse: FetchTicketsResponse = response
  return {
    ...ticketResponse,
    treeValue: [ticketResponse.uuid],
    cumulation: ticketResponse.wbsItem?.cumulation,
    wbsItem: {
      ...ticketResponse.wbsItem,
      wbsItemType: ticketResponse.wbsItem?.typeDto
        ? new WbsItemTypeVO(ticketResponse.wbsItem.typeDto)
        : store
            .getState()
            .project.wbsItemTypes.get(ticketResponse.wbsItem.type)!,
      baseWbsItemType: ticketResponse.wbsItem?.baseTypeDto
        ? new WbsItemTypeVO(ticketResponse.wbsItem.baseTypeDto)
        : store
            .getState()
            .project.wbsItemTypes.get(ticketResponse.wbsItem.type)!,
      ticketType:
        ticketResponse.wbsItem?.ticketType === CUSTOM_ENUM_NONE
          ? undefined
          : ticketResponse.wbsItem.ticketType,
      status: ticketResponse.wbsItem?.status
        ? (ticketResponse.wbsItem.status as WbsItemStatus)
        : undefined,
      substatus:
        ticketResponse.wbsItem?.substatus === CUSTOM_ENUM_NONE
          ? undefined
          : ticketResponse.wbsItem.substatus,
      priority:
        ticketResponse.wbsItem?.priority === CUSTOM_ENUM_NONE
          ? undefined
          : ticketResponse.wbsItem?.priority,
      difficulty:
        ticketResponse.wbsItem?.difficulty === CUSTOM_ENUM_NONE
          ? undefined
          : ticketResponse.wbsItem.difficulty,
      description: ticketResponse.wbsItem?.description ?? '',
      tags: ticketResponse.wbsItem?.tags,
      sprint: ticketResponse.wbsItem?.sprint,
      estimatedWorkload: Workload.from({
        hour: ticketResponse.wbsItem?.estimatedHour,
        standard: {
          dailyWorkHours,
          monthlyWorkDays,
        },
      }),
      additionalPropertyValues: ticketResponse.wbsItem?.additionalPropertyValues
        ? wbsItemAdditionalPropertyValuesVoService.of(
            ticketResponse.wbsItem.additionalPropertyValues
          )
        : undefined,
    },
  } as TicketRow
}

export const createUpdateTicketDeltaBatchRequest = (
  projectUuid: string,
  changedRows: TicketRow[],
  originalData: TicketRow[],
  currentData: TicketRow[],
  deletedRows: TicketRow[]
) => {
  const changed = changedRows

  const added = generateCreateRequest(
    projectUuid,
    currentData,
    changed,
    ({
      ticketRequest,
    }: GenerateCreateRequestParams): CreateTicketInput | undefined => {
      return ticketRequest
    }
  )

  const edited = generateUpdateDeltaBatchRequest(
    originalData,
    currentData,
    changed,
    ({
      ticketRequest,
    }: GenerateUpdateRequestParams): UpdateTicketDeltaInput | undefined => {
      return ticketRequest
    }
  )

  const deleted = gernerateTicketDeleteRequest(
    deletedRows,
    (row: TicketRow) => {
      return {
        uuid: row.uuid,
        lockVersion: row.lockVersion,
      }
    }
  )
  const watchers = generateUpdateWathersRequest(originalData, changed)

  const tags = changed
    .filter((row: TicketRow) => {
      const original =
        originalData.find(v => v.uuid === row.uuid)?.wbsItem?.tags ?? []
      const newData = row.wbsItem?.tags ?? []
      return (
        (row.added || row.edited) &&
        row.wbsItem &&
        isTagColumnEdited(original, newData)
      )
    })
    .map((row: TicketRow) => {
      const wbsItem: TicketWbsItemRow = row.wbsItem!
      return {
        uuid: row.uuid,
        wbsItemUuid: wbsItem.uuid,
        tagUuids:
          wbsItem.tags && Array.isArray(wbsItem.tags)
            ? wbsItem.tags.map(v => v.uuid)
            : [],
      }
    })

  return {
    added,
    edited,
    deleted,
    watchers,
    tags,
  }
}

export interface FetchTicketsWbsItemResponse extends WbsItemDetail {
  cumulation?: ProjectPlanCumulation
}

type FetchTicketsResponse = {
  uuid: string
  lockVersion: number
  ticketType: string
  wbsItem: FetchTicketsWbsItemResponse
  ticketList: TicketListBasic
  parentWbsItem: WbsItemBasic
  parentUuid?: string
  prevSiblingUuid?: string
  commentSummary?: CommentSummary
  deliverableAttachmentSummary?: AttachmentSummary
  parentPath?: string
  revision?: string
  createdAt?: number
  createdBy?: UserBasic
  updatedAt?: number
  updatedBy?: UserBasic
}
